import { BiDateWindow, BiTimeGrain } from '@euler/model/bi/query';
import { BiFieldType, BiResultColumn } from '@euler/model/bi/result';

const kGrainSuffix = /__(day|week|month|quarter|year)$/;

/**
 * The grain a dimension column is bucketed to: the server names a bucketed
 * column `<field>__<grain>` and types it as text; a plain date column is
 * by day. Null for anything else, a datetime's wall times included.
 */
export function biBucketGrainOf(column: BiResultColumn): BiTimeGrain | null {
  const suffix = kGrainSuffix.exec(column.key)?.[1];
  if (suffix) return suffix as BiTimeGrain;
  return column.type === BiFieldType.Date ? BiTimeGrain.Day : null;
}

const pad = (value: number) => String(value).padStart(2, '0');

function dayLabel(date: Date): string {
  return (
    `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-` +
    pad(date.getUTCDate())
  );
}

/** A day's bucket label at a grain, as the server's time buckets read. */
function bucketOf(date: Date, grain: BiTimeGrain): string {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  switch (grain) {
    case BiTimeGrain.Day:
      return dayLabel(date);
    case BiTimeGrain.Week: {
      // Labelled by the Monday that starts the ISO week.
      const monday = new Date(date);
      monday.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7));
      return dayLabel(monday);
    }
    case BiTimeGrain.Month:
      return `${year}-${pad(month + 1)}`;
    case BiTimeGrain.Quarter:
      return `${year}-Q${Math.floor(month / 3) + 1}`;
    case BiTimeGrain.Year:
      return String(year);
  }
}

/**
 * The buckets a date window covers at a grain, in order, at most `limit`
 * of them (enough to tell how many there are). Empty for a window that
 * does not read as dates.
 */
export function biWindowBuckets(
  window: BiDateWindow | undefined,
  grain: BiTimeGrain,
  limit: number,
): string[] {
  if (!window) return [];
  const from = new Date(`${window.from}T00:00:00Z`);
  const to = new Date(`${window.to}T00:00:00Z`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return [];
  const buckets: string[] = [];
  for (
    let day = from;
    day <= to && buckets.length < limit;
    day = new Date(day.getTime() + 86400000)
  ) {
    const bucket = bucketOf(day, grain);
    if (buckets[buckets.length - 1] !== bucket) buckets.push(bucket);
  }
  return buckets;
}
